import type { CallbackInterface } from 'recoil'

import getIntl from 'Helpers/intl'
import { logAppError, logAppInfo } from 'Helpers/logger'
import { errorUpdater, loaderUpdater, pageState } from 'State/page'
import type { PageStateType } from 'Types/state'
import {
  TOAST_VARIANT_ERROR,
  TOAST_VARIANT_SUCCESS,
  toastShow,
} from 'mailup-web-components'

import type {
  GroupItem,
  GroupOperation,
  ModalCopyState,
} from 'Pages/GroupsList/model'
import { fetchResultsCallback } from 'Pages/GroupsList/recoil/callbacks/callbacks'
import {
  defaultModalCopyState,
  modalCopyState,
  modalDeleteState,
  selectionState,
  userVisibleListsState,
} from 'Pages/GroupsList/recoil/state/state'
import {
  notifyDeleteSuccess,
  notifyGroupEntities,
} from 'Pages/GroupsList/services/notifications'
import {
  checkGroupNameExistsInList,
  copyGroupToLists,
  deleteOrEmptyGroups,
  fetchEntities,
  getAllUserVisibileLists,
} from 'Pages/GroupsList/services/services'

interface ModalDeleteOpenCallbackIn {
  groups: GroupItem[]
  op: GroupOperation
}

export const modalDeleteOpenCallback =
  ({ set }: CallbackInterface) =>
  async ({ groups, op }: ModalDeleteOpenCallbackIn) => {
    if (op === 'DeleteGroup' || op === 'DeleteGroupAndRecipients') {
      const entitiesArray = await Promise.all(
        groups.map((group) => fetchEntities(group.Id)),
      )

      let atLeastOneGroupHasEntities = false
      for (const [i, entities] of entitiesArray.entries()) {
        if (entities.length > 0) {
          notifyGroupEntities(entities, groups[i]?.Name)
          atLeastOneGroupHasEntities = true
        }
      }
      if (atLeastOneGroupHasEntities) {
        return
      }
    }

    set(modalDeleteState, {
      show: true,
      op,
      items: groups,
    })
  }

export const modalDeleteCancelCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(modalDeleteState)
  }

export const modalDeleteConfirmCallback =
  ({ reset, set, snapshot, ...cbi }: CallbackInterface) =>
  async () => {
    const { items, op } = await snapshot.getPromise(modalDeleteState)

    try {
      if (items !== undefined && items.length > 0 && op !== undefined) {
        logAppInfo({
          Component: 'GroupsList',
          Method: 'modalDeleteConfirmCallback',
          Message: 'Inizio cancellazione gruppo/i',
          Data: JSON.stringify({
            operation: op.toString(),
            ids: items.map((item) => item.Id).toString(),
          }),
        })
        await deleteOrEmptyGroups(
          items.map((item) => item.Id),
          op,
        )
        logAppInfo({
          Component: 'GroupsList',
          Method: 'modalDeleteConfirmCallback',
          Message: 'Fine cancellazione gruppo/i',
          Data: JSON.stringify({
            operation: op.toString(),
            ids: items.map((item) => item.Id).toString(),
          }),
        })
        reset(selectionState)
        if (op === 'DeleteGroup' || op === 'DeleteGroupAndRecipients') {
          await fetchResultsCallback({ ...cbi, reset, set, snapshot })(
            {
              pageNumber: 1,
            },
            {},
          )
        }
        notifyDeleteSuccess(op, items.length > 1)
      }
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'GroupsList',
        Method: 'modalDeleteConfirmCallback',
        Message: 'Errore in cancellazione gruppo/i',
        appError: error,
      })

      set<PageStateType>(pageState, errorUpdater)
    }

    reset(modalDeleteState)
  }

/**
 * COPY MODAL
 * */
export const modalCopyCancelCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(modalCopyState)
  }

export const modalCopyOpenCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (group: GroupItem) => {
    const hasLists = await snapshot.getPromise(userVisibleListsState)
    if (!hasLists.length) {
      set<PageStateType>(pageState, loaderUpdater(true))
      try {
        const userVisibleLists = await getAllUserVisibileLists()
        set(userVisibleListsState, userVisibleLists)
        set<PageStateType>(pageState, (prev) => ({ ...prev, status: 'ready' }))
      } catch (error) {
        console.error('### error', error)
        logAppError({
          Component: 'GroupsList',
          Method: 'modalCopyOpenCallback',
          appError: error,
        })
        set<PageStateType>(pageState, errorUpdater)
      }
      set<PageStateType>(pageState, loaderUpdater(false))
    }

    set(modalCopyState, {
      ...defaultModalCopyState,
      show: true,
      group: group,
      newName: group.Name,
    })
  }

export const modalCopyNextStepCallback =
  ({ set, reset, snapshot }: CallbackInterface) =>
  async () => {
    const {
      step: prevStep,
      newName,
      newList,
      group,
    } = await snapshot.getPromise(modalCopyState)
    if (prevStep === 'form') {
      set(modalCopyState, (prev) => ({ ...prev, step: 'confirm' as const }))
    } else {
      try {
        if (!group?.Id || !newList?.IdList) {
          const error = new Error('group or list undefined')
          console.error('### error', error)
          logAppError({
            Component: 'GroupsList',
            Method: 'modalCopyNextStepCallback',
            appError: error,
          })
          return
        }
        await copyGroupToLists(group.Id, [newList.IdList], newName)
        reset(modalCopyState)
        toastShow(
          getIntl().formatMessage({ id: 'Load.CopyGroupToList.SuccessToast' }),
          TOAST_VARIANT_SUCCESS,
        )
      } catch (error) {
        console.error('### error', error)
        logAppError({
          Component: 'GroupsList',
          Method: 'modalCopyNextStepCallback',
          appError: error,
        })
        toastShow(
          getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
          TOAST_VARIANT_ERROR,
        )
      }
    }
  }

export const modalCopyChangeFieldCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (p: Partial<ModalCopyState>) => {
    set(modalCopyState, (prev) => ({
      ...prev,
      ...p,
      nameValidLoading: true,
    }))
  }

export const modalCopyCheckNameCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (p: Partial<ModalCopyState>) => {
    const { newName: oldName, newList: oldList } =
      await snapshot.getPromise(modalCopyState)
    const name = p.newName ?? oldName
    const list = p.newList ?? oldList
    let nameExists = false
    if (list) {
      nameExists = await checkGroupNameExistsInList(name, list.IdList)
    }
    set(modalCopyState, (prev) => ({
      ...prev,
      nameValid: !nameExists,
      nameValidLoading: false,
    }))
  }
